/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Sélecteur de mode discret et moderne :
 * Bascule entre "Recherche documentaire" (classique) et "Assistant juridique IA ✨".
 */

import React from 'react';
import { Sparkles, BookOpen } from 'lucide-react';

interface AiModeToggleProps {
  isAiMode: boolean;
  onToggle: () => void;
  variant?: 'hero' | 'search';
  className?: string;
}

export const AiModeToggle: React.FC<AiModeToggleProps> = ({
  isAiMode,
  onToggle,
  variant = 'search',
  className = '',
}) => {
  const isHero = variant === 'hero';

  return (
    <div className={`inline-flex items-center ${className}`}>
      <button
        type="button"
        role="switch"
        aria-checked={isAiMode}
        aria-label="Basculer vers l'assistant juridique IA"
        onClick={onToggle}
        className={`group relative inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer select-none ${
          isAiMode
            ? isHero
              ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-md shadow-violet-900/40 ring-2 ring-white/40'
              : 'bg-gradient-to-r from-[#1C0132] to-[#3D0F73] text-white shadow-md shadow-primary-750/25 ring-1 ring-primary-750/30'
            : isHero
              ? 'bg-white/85 hover:bg-white text-zinc-800 border border-white/50 shadow-xs backdrop-blur-xs'
              : 'bg-white hover:bg-zinc-50 text-zinc-700 border border-zinc-200/90 shadow-2xs hover:border-zinc-300'
        }`}
      >
        {/* Animated sparkle icon */}
        <span
          className={`flex items-center justify-center w-4 h-4 rounded-full transition-transform ${
            isAiMode ? 'text-amber-300 scale-110' : 'text-violet-600 group-hover:scale-110'
          }`}
        >
          {isAiMode ? <Sparkles className="w-3.5 h-3.5 fill-current" /> : <Sparkles className="w-3.5 h-3.5" />}
        </span>

        {/* Text Label */}
        <span className="tracking-tight">
          {isAiMode ? (
            <span className="font-bold flex items-center gap-1">
              <span>Mode IA actif</span>
              <span className="text-[10px] bg-white/20 text-white px-1.5 py-0.2 rounded-full font-mono uppercase">
                Pro
              </span>
            </span>
          ) : (
            <span className="flex items-center gap-1 font-medium">
              <span>Activer le mode IA</span>
              <span className="text-amber-500 font-bold">✨</span>
            </span>
          )}
        </span>

        {/* Subtle switch track / thumb */}
        <span
          className={`w-7 h-4 flex items-center rounded-full p-0.5 transition-colors ${
            isAiMode ? 'bg-amber-400/30 justify-end' : 'bg-zinc-200/80 justify-start'
          }`}
        >
          <span
            className={`w-3 h-3 rounded-full transition-transform ${
              isAiMode ? 'bg-amber-300 shadow-xs' : 'bg-white shadow-xs'
            }`}
          />
        </span>
      </button>
    </div>
  );
};

export default AiModeToggle;
